import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BsPlusLg } from "react-icons/bs";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import Handler from "../../Helpers/Handler";
import { formatDate, formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton } from "../../Components/Needed";
import EmptyState from "../../Components/List/EmptyState";
import ErrorState from "../../Components/List/ErrorState";
import useListParams from "../../Helpers/useListParams";
import { LeaveCancelModal, CancelRequestChip, LEAVE_STATUS_CLASS, cancellableDays, hasWaitingCancelRequest, leaveTypeText } from "../../Components/LeaveCancel";

const LEAVE_TYPES = [
    { value: "CASUAL_LEAVE", label: "Casual leave" },
    { value: "SICK_LEAVE", label: "Sick leave" },
    { value: "EMERGENCY_LEAVE", label: "Emergency leave" },
    { value: "HALF_DAY", label: "Half day" },
];

const STATUS_CLASS = LEAVE_STATUS_CLASS;
const HALVES = [
    { value: "FIRST_HALF", label: "First half" },
    { value: "SECOND_HALF", label: "Second half" },
];
const COUNT = 10;

const toIso = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const emptyForm = { type: LEAVE_TYPES[0].value, session: "", reason: "" };
// Two dates, not one. Seeded with a single date, the picker treats the very
// first click as the END of a range starting today and the second click as the
// start of a brand new one - so a two-day span could never be selected. A
// complete (single-day) range means any two clicks read as start-then-end,
// while the field still opens on today so a same-day leave needs no clicks.
const emptyRange = () => [new DateObject(), new DateObject()];

const LeaveTab = () => {
    const [list, setList] = useState([]);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Page number in the URL path, the status filter in the query string (next to the page's ?tab=) - see Helpers/useListParams.
    const { page, setPage, filters, setFilter } = useListParams({ status: "" }, { zeroBased: true });
    const statusFilter = filters.status;
    const setStatusFilter = (value) => setFilter("status", value);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    // The picker's own value, held exactly as it hands it back. Deriving
    // from/to into form state on every click was what stopped a two-day range
    // from ever completing: writing the end date on the first click made the
    // range look finished, so the second click started a new one instead.
    const [range, setRange] = useState(emptyRange);
    const [saving, setSaving] = useState(false);
    // The row being withdrawn / asked to be cancelled, and which of the two it is.
    const [cancelTarget, setCancelTarget] = useState(null);

    const load = useCallback(async (pageNo) => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/staff/leave/list/${pageNo}`,
            params: { count: COUNT },
            data: { ...(statusFilter ? { status: statusFilter } : {}), sortBy: "createdAt", sort: true },
        });
        if (response?.success && response?.data?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setHasNext(Boolean(data.next));
            setError(null);
        } else {
            setList([]);
            setError(response?.data?.message || "Unable to load your leave requests");
        }
        setLoading(false);
    }, [statusFilter]);

    useEffect(() => { load(page); }, [load, page]);

    // One date picked is a single-day leave; two is the span between them.
    const fromDate = range?.[0] ? toIso(range[0].toDate()) : "";
    const toDate = range?.[1] ? toIso(range[1].toDate()) : fromDate;
    const dayCount = fromDate
        ? Math.round((new Date(`${toDate}T00:00:00`) - new Date(`${fromDate}T00:00:00`)) / 86400000) + 1
        : 0;

    const submit = async () => {
        if (!fromDate) { toast.error("Pick your leave dates"); return; }
        if (form.type === "HALF_DAY" && dayCount > 1) { toast.error("A half day covers a single day"); return; }
        if (form.type === "HALF_DAY" && !form.session) { toast.error("Choose the first half or the second half"); return; }
        if (!form.reason.trim()) { toast.error("Please give a reason"); return; }
        setSaving(true);
        const response = await Handler({
            method: "post",
            url: "/staff/leave/register",
            data: { type: form.type, ...(form.type === "HALF_DAY" ? { session: form.session } : {}), fromDate, toDate, reason: form.reason.trim() },
        });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || "Leave applied");
            setForm(emptyForm);
            setRange(emptyRange());
            setShowForm(false);
            await load(0);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to apply for leave"));
        }
        setSaving(false);
    };

    const change = (event) => setForm((prev) => ({ ...prev, [event.target.name]: event.target.value, ...(event.target.name === "type" && event.target.value !== "HALF_DAY" ? { session: "" } : {}) }));

    return (
        <div className="card att-panel-card">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                    <h6 className="fw-semibold mb-0">Leave requests</h6>
                    <div className="d-flex gap-2">
                        <select
                            className="form-select form-select-sm att-filter-select"
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            aria-label="Filter by status"
                        >
                            <option value="">All statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                        <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => setShowForm((prev) => !prev)}>
                            <BsPlusLg /> Apply
                        </button>
                    </div>
                </div>

                {showForm ? (
                    <div className="att-request-form">
                        <div className="row g-3">
                            <div className="col-12 col-md-3">
                                <label className="form-label" htmlFor="leave-type">Type</label>
                                <select id="leave-type" name="type" className="form-select" value={form.type} onChange={change}>
                                    {LEAVE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                </select>
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Dates</label>
                                {/* One range picker instead of two date inputs - picking a
                                    span is what applying for leave actually is, and the
                                    picker can't produce an end before the start. */}
                                <DatePicker
                                    range
                                    numberOfMonths={1}
                                    dateSeparator="  to  "
                                    format="YYYY-MM-DD"
                                    minDate={new Date()}
                                    containerClassName="w-100"
                                    inputClass="form-control"
                                    value={range}
                                    onChange={setRange}
                                />
                                <div className="form-text">
                                    {dayCount > 1
                                        ? `${dayCount} days — click the first day, then the last`
                                        : "Click the first day, then the last. One day is fine too."}
                                </div>
                            </div>
                            <div className="col-12 col-md-3 d-flex align-items-center">
                                <button type="button" className="btn btn-primary w-100" disabled={saving} onClick={submit}>
                                    {saving ? "Submitting..." : "Submit request"}
                                </button>
                            </div>
                            {form.type === "HALF_DAY" ? (
                                <div className="col-12">
                                    <div className="form-label" id="leave-half-label">Which half?</div>
                                    <div className="d-flex gap-4" role="radiogroup" aria-labelledby="leave-half-label">
                                        {HALVES.map((half) => (
                                            <div className="form-check" key={half.value}>
                                                <input className="form-check-input" type="radio" name="session" id={`leave-half-${half.value}`} value={half.value} checked={form.session === half.value} onChange={change} />
                                                <label className="form-check-label" htmlFor={`leave-half-${half.value}`}>{half.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            <div className="col-12">
                                {["SICK_LEAVE", "EMERGENCY_LEAVE", "HALF_DAY"].includes(form.type) ? (
                                    <div className="att-inline-note mb-2">
                                        Sick, emergency and half-day leave can be applied for today — including after you've already checked in.
                                    </div>
                                ) : null}
                                <label className="form-label" htmlFor="leave-reason">Reason</label>
                                <textarea
                                    id="leave-reason"
                                    name="reason"
                                    className="form-control"
                                    rows={2}
                                    maxLength={500}
                                    placeholder="Why do you need this leave?"
                                    value={form.reason}
                                    onChange={change}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}

                {loading ? (
                    <>{[0, 1, 2].map((i) => <Skeleton key={i} height={24} className="mb-2" />)}</>
                ) : error ? (
                    <ErrorState message={error} onRetry={() => load(0)} />
                ) : !list.length ? (
                    <EmptyState reason={statusFilter ? "filter" : "empty"} title="No leave requests" description="Requests you submit will appear here with their approval status." />
                ) : (
                    <>
                        <div className={`list-table-wrap`}>
                            <div className='table-responsive list-table-scroll'>
                                <table className='table align-middle mb-0 list-table'>
                                    <thead className='sticky-top'>
                                        <tr>
                                        <th className="py-3 px-3 list-th ">Type</th>
                                        <th className="py-3 px-3 list-th ">Dates</th>
                                        <th className="py-3 px-3 list-th ">Reason</th>
                                        <th className="py-3 px-3 list-th ">Status</th>
                                        <th className="py-3 px-3 list-th ">Decided by</th>
                                        <th className="py-3 px-3 list-th ">Applied</th>
                                        <th className="py-3 px-3 list-th ">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((row) => (
                                            <tr key={row.leaveId}>
                                                {/* <td className="fw-semibold">{row.leaveId}</td> */}
                                                <td>{leaveTypeText(row)}</td>
                                                <td>{row.type === "HALF_DAY" ? formatDate(row.fromDate) : <>{formatDate(row.fromDate)} → {formatDate(row.toDate)}</>}</td>
                                                <td className="att-reason-cell">
                                                    {row.reason || "-"}
                                                    {row.status === "CANCELLED" && row.cancelReason ? <div className="text-muted small">Cancelled: {row.cancelReason}</div> : null}
                                                    {row.cancelRequest?.status === "PENDING" ? <div className="text-muted small">Cancel request: {row.cancelRequest.reason}</div> : null}
                                                </td>
                                                <td>
                                                    <span className={`att-chip ${STATUS_CLASS[row.status] || "att-muted"}`}>{textCapitalize(row.status)}</span>
                                                    <CancelRequestChip row={row} />
                                                </td>
                                                <td>{row.approvedBy || "-"}</td>
                                                <td className="text-muted">{formatDateTime(row.createdAt)}</td>
                                                <td>
                                                    {row.status === "PENDING" ? (
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelTarget({ row, mode: "withdraw" })}>Withdraw</button>
                                                    ) : row.status === "APPROVED" && cancellableDays(row) && !hasWaitingCancelRequest(row) ? (
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelTarget({ row, mode: "request" })}>Cancel leave</button>
                                                    ) : <span className="text-muted small">-</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between mt-3">
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!hasNext} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    </>
                )}
            </div>
            {cancelTarget ? (
                <LeaveCancelModal
                    leave={cancelTarget.row}
                    mode={cancelTarget.mode}
                    url={`/staff/leave/${cancelTarget.row.leaveId}/cancel`}
                    onClose={() => setCancelTarget(null)}
                    onDone={() => { setCancelTarget(null); load(page); }}
                />
            ) : null}
        </div>
    );
};

export default LeaveTab;
